import {useMutation, UseMutationResult, useQuery, useQueryClient} from '@tanstack/react-query';
import {EditStoryParams, StoryDetailedModel} from '../model';
import {blocksApi} from '../api';
import {useNotifications} from '../../../shared';

type BlockStoryByIdState = {
  data: StoryDetailedModel | undefined;
  error: Error | null;
  isLoading: boolean;
  updateStoryMutation: UseMutationResult<unknown, Error, EditStoryParams>;
  deleteStoryMutation: UseMutationResult<unknown, Error, {blockId: string; storyId: string}>;
  refetchStory: () => void;
};

export const useBlockStoryById = (blockId: string, storyId: string): BlockStoryByIdState => {
  const queryClient = useQueryClient();
  const {createNotification} = useNotifications();

  const getStoryById = useQuery<StoryDetailedModel, Error>({
    queryKey: ['story', blockId, storyId],
    queryFn: () => blocksApi.getStoryById(blockId, storyId),
    enabled: !!blockId && !!storyId,
    staleTime: 60 * 1000,
  });

  const refetchStory = () => {
    getStoryById.refetch();
  };

  const updateStoryMutation = useMutation({
    mutationFn: blocksApi.updateStory,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['block-stories', 'infinite'], refetchType: 'all'});
      queryClient.invalidateQueries({queryKey: ['story', blockId, storyId], refetchType: 'all'});
      createNotification({
        title: 'История успешно обновлена',
        name: 'updateStory',
        theme: 'success',
      });
    },
  });

  const deleteStoryMutation = useMutation({
    mutationFn: ({blockId, storyId}: {blockId: string; storyId: string}) => blocksApi.deleteStory(blockId, storyId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['block-stories', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'История успешно удалена',
        name: 'deleteStory',
        theme: 'success',
      });
    },
  });

  return {
    data: getStoryById.data,
    error: getStoryById.error,
    isLoading: getStoryById.isLoading,
    updateStoryMutation,
    deleteStoryMutation,
    refetchStory,
  };
};
